import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Users, UserPlus, UserMinus, Link2, Unlink, Home, MessageSquare, Send, Bell } from 'lucide-react';

const TYPE_LABELS = { PARTICIPANT_TEAM: 'Participant', SIL_HOUSE_TEAM: 'SIL House', ACCOMMODATION_TEAM: 'Accommodation', SUPPORT_COORDINATION: 'Support Coord', CLINICAL_TEAM: 'Clinical', ROSTERING_TEAM: 'Rostering', CUSTOM: 'Custom' };
const ROLE_LABELS = { TEAM_LEADER: 'Team Leader', PRIMARY_SUPPORT_WORKER: 'Primary Support', SECONDARY_SUPPORT_WORKER: 'Secondary Support', COORDINATOR: 'Coordinator', CLINICAL_STAFF: 'Clinical', ADMIN_SUPPORT: 'Admin', CASUAL_POOL: 'Casual Pool' };

export default function TeamDetailPage() {
  const { teamId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showLinkParticipant, setShowLinkParticipant] = useState(false);
  const [showLinkHouse, setShowLinkHouse] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [allStaff, setAllStaff] = useState([]);
  const [allParticipants, setAllParticipants] = useState([]);
  const [allHouses, setAllHouses] = useState([]);
  const [messages, setMessages] = useState([]);

  // Add member form
  const [memberForm, setMemberForm] = useState({ staff_id: '', team_role: 'PRIMARY_SUPPORT_WORKER', is_team_leader: false });
  const [linkPartId, setLinkPartId] = useState('');
  const [linkHouseId, setLinkHouseId] = useState('');
  const [msgForm, setMsgForm] = useState({ subject: '', message_body: '', message_type: 'GENERAL' });

  const loadTeam = useCallback(async () => {
    try {
      const { data } = await api.get(`/teams/${teamId}`);
      setTeam(data.data);
    } catch { toast.error('Failed to load team'); }
    setLoading(false);
  }, [teamId]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  useEffect(() => {
    Promise.allSettled([
      api.get('/staff?pageSize=100'),
      api.get('/participants?pageSize=100'),
      api.get('/houses?pageSize=100'),
      api.get(`/teams/${teamId}/messages?pageSize=20`),
    ]).then(([sR, pR, hR, mR]) => {
      if (sR.status === 'fulfilled') setAllStaff(sR.value.data.items || sR.value.data.data || []);
      if (pR.status === 'fulfilled') setAllParticipants(pR.value.data.items || pR.value.data.data || []);
      if (hR.status === 'fulfilled') setAllHouses(hR.value.data.items || hR.value.data.data || []);
      if (mR.status === 'fulfilled') setMessages(mR.value.data.items || []);
    });
  }, [teamId]);

  const handleAddMember = async () => {
    if (!memberForm.staff_id) { toast.error('Select a staff member'); return; }
    try {
      await api.post(`/teams/${teamId}/members`, memberForm);
      toast.success('Member added');
      setShowAddMember(false);
      setMemberForm({ staff_id: '', team_role: 'PRIMARY_SUPPORT_WORKER', is_team_leader: false });
      loadTeam();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await api.delete(`/teams/${teamId}/members/${memberId}`);
      toast.success('Member removed');
      loadTeam();
    } catch { toast.error('Failed'); }
  };

  const handleLinkParticipant = async () => {
    if (!linkPartId) return;
    try {
      await api.post(`/teams/${teamId}/participants`, { participant_id: linkPartId });
      toast.success('Participant linked');
      setShowLinkParticipant(false);
      setLinkPartId('');
      loadTeam();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleUnlinkParticipant = async (pid) => {
    try {
      await api.delete(`/teams/${teamId}/participants/${pid}`);
      toast.success('Participant unlinked');
      loadTeam();
    } catch { toast.error('Failed'); }
  };

  const handleLinkHouse = async () => {
    if (!linkHouseId) return;
    try {
      await api.post(`/teams/${teamId}/houses`, { house_id: linkHouseId });
      toast.success('House linked');
      setShowLinkHouse(false);
      setLinkHouseId('');
      loadTeam();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleUnlinkHouse = async (hid) => {
    try {
      await api.delete(`/teams/${teamId}/houses/${hid}`);
      toast.success('House unlinked');
      loadTeam();
    } catch { toast.error('Failed'); }
  };

  const handleSendMessage = async () => {
    if (!msgForm.message_body.trim()) { toast.error('Message is required'); return; }
    try {
      const { data } = await api.post(`/teams/${teamId}/messages`, msgForm);
      toast.success(`Message sent to ${data.data.recipients} members`);
      setShowMessage(false);
      setMsgForm({ subject: '', message_body: '', message_type: 'GENERAL' });
      // Reload messages
      const mR = await api.get(`/teams/${teamId}/messages?pageSize=20`);
      setMessages(mR.data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;
  if (!team) return <div className="text-center py-20 text-muted-foreground">Team not found</div>;

  const defaultTab = searchParams.get('tab') || 'overview';

  return (
    <div className="space-y-6" data-testid="team-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/teams')}><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">{team.team_name}</h1>
              <Badge variant="outline">{TYPE_LABELS[team.team_type] || team.team_type}</Badge>
              <Badge>{team.status}</Badge>
            </div>
            {team.description && <p className="text-muted-foreground text-sm mt-0.5">{team.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowMessage(true)} data-testid="send-message-btn"><MessageSquare className="w-4 h-4 mr-2" />Message Team</Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/teams/${teamId}/edit`)}><Edit className="w-4 h-4 mr-2" />Edit</Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold">{team.members?.length || 0}</p><p className="text-xs text-muted-foreground">Members</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold">{team.participants?.length || 0}</p><p className="text-xs text-muted-foreground">Participants</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold">{team.houses?.length || 0}</p><p className="text-xs text-muted-foreground">Houses</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold">{team.leader_name || '-'}</p><p className="text-xs text-muted-foreground">Leader</p></CardContent></Card>
      </div>

      <Tabs defaultValue={defaultTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members">Members ({team.members?.length || 0})</TabsTrigger>
          <TabsTrigger value="participants" data-testid="tab-participants">Participants ({team.participants?.length || 0})</TabsTrigger>
          <TabsTrigger value="houses" data-testid="tab-houses">Houses ({team.houses?.length || 0})</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <Card><CardContent className="py-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div><span className="text-muted-foreground">Team Code:</span><br/><span className="font-medium">{team.team_code || '-'}</span></div>
              <div><span className="text-muted-foreground">Type:</span><br/><span className="font-medium">{TYPE_LABELS[team.team_type]}</span></div>
              <div><span className="text-muted-foreground">Status:</span><br/><Badge>{team.status}</Badge></div>
              <div><span className="text-muted-foreground">State:</span><br/><span className="font-medium">{team.state_id || 'Org-wide'}</span></div>
              <div><span className="text-muted-foreground">Branch:</span><br/><span className="font-medium">{team.branch_id || 'Org-wide'}</span></div>
              <div><span className="text-muted-foreground">Leader:</span><br/><span className="font-medium">{team.leader_name || '-'}</span></div>
            </div>
            <div className="mt-4 flex items-center gap-4 text-xs">
              <Badge variant={team.shift_notifications ? 'default' : 'outline'}><Bell className="w-3 h-3 mr-1" />Shift Alerts {team.shift_notifications ? 'ON' : 'OFF'}</Badge>
              <Badge variant={team.participant_notifications ? 'default' : 'outline'}>Participant Alerts {team.participant_notifications ? 'ON' : 'OFF'}</Badge>
              <Badge variant={team.house_notifications ? 'default' : 'outline'}>House Alerts {team.house_notifications ? 'ON' : 'OFF'}</Badge>
              <Badge variant={team.messaging_enabled ? 'default' : 'outline'}>Messaging {team.messaging_enabled ? 'ON' : 'OFF'}</Badge>
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Team Members</h3>
            <Button size="sm" onClick={() => setShowAddMember(true)} data-testid="add-member-btn"><UserPlus className="w-4 h-4 mr-2" />Add Member</Button>
          </div>
          {(team.members || []).length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">No members yet</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {(team.members || []).filter(m => m.is_active).map(m => (
                <Card key={m.id} data-testid={`member-${m.id}`}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{(m.staff_name || '??')[0]}</div>
                      <div>
                        <p className="text-sm font-medium">{m.staff_name || m.staff_id}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-[10px]">{ROLE_LABELS[m.team_role] || m.team_role}</Badge>
                          {m.is_team_leader && <Badge className="text-[10px]">Leader</Badge>}
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleRemoveMember(m.id)} data-testid={`remove-member-${m.id}`}><UserMinus className="w-4 h-4" /></Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Participants Tab */}
        <TabsContent value="participants" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Linked Participants</h3>
            <Button size="sm" onClick={() => setShowLinkParticipant(true)} data-testid="link-participant-btn"><Link2 className="w-4 h-4 mr-2" />Link Participant</Button>
          </div>
          {(team.participants || []).length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">No participants linked</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {(team.participants || []).map(p => (
                <Card key={p.id} data-testid={`participant-${p.participant_id}`}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{p.participant_name || p.participant_id}</p>
                        {p.ndis_number && <p className="text-xs text-muted-foreground">NDIS: {p.ndis_number}</p>}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleUnlinkParticipant(p.participant_id)}><Unlink className="w-4 h-4" /></Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Houses Tab */}
        <TabsContent value="houses" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Linked Houses</h3>
            <Button size="sm" onClick={() => setShowLinkHouse(true)} data-testid="link-house-btn"><Link2 className="w-4 h-4 mr-2" />Link House</Button>
          </div>
          {(team.houses || []).length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">No houses linked</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {(team.houses || []).map(h => (
                <Card key={h.id} data-testid={`house-${h.house_id}`}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Home className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{h.house_name || h.house_id}</p>
                        {h.house_address && <p className="text-xs text-muted-foreground">{h.house_address}</p>}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleUnlinkHouse(h.house_id)}><Unlink className="w-4 h-4" /></Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Team Messages</h3>
            <Button size="sm" onClick={() => setShowMessage(true)}><Send className="w-4 h-4 mr-2" />Send Message</Button>
          </div>
          {messages.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">No messages yet</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {messages.map(m => (
                <Card key={m.id} data-testid={`message-${m.id}`}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{m.sender_name || 'Unknown'}</span>
                        {m.message_type === 'URGENT' && <Badge variant="destructive" className="text-[10px]">URGENT</Badge>}
                        {m.message_type === 'ANNOUNCEMENT' && <Badge className="text-[10px]">Announcement</Badge>}
                      </div>
                      <span className="text-xs text-muted-foreground">{m.created_at?.slice(0, 16).replace('T', ' ')}</span>
                    </div>
                    {m.subject && <p className="text-sm font-medium">{m.subject}</p>}
                    <p className="text-sm text-muted-foreground mt-1">{m.message_body}</p>
                    {m.unread_count > 0 && <Badge variant="outline" className="mt-1 text-[10px]">{m.unread_count} unread</Badge>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Member Dialog */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Staff Member *</Label>
              <Select value={memberForm.staff_id} onValueChange={v => setMemberForm(p => ({ ...p, staff_id: v }))}>
                <SelectTrigger data-testid="member-staff-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>{allStaff.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Role</Label>
              <Select value={memberForm.team_role} onValueChange={v => setMemberForm(p => ({ ...p, team_role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMember(false)}>Cancel</Button>
            <Button onClick={handleAddMember} data-testid="confirm-add-member">Add Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Participant Dialog */}
      <Dialog open={showLinkParticipant} onOpenChange={setShowLinkParticipant}>
        <DialogContent>
          <DialogHeader><DialogTitle>Link Participant</DialogTitle></DialogHeader>
          <div><Label>Participant *</Label>
            <Select value={linkPartId} onValueChange={setLinkPartId}>
              <SelectTrigger data-testid="link-participant-select"><SelectValue placeholder="Select participant" /></SelectTrigger>
              <SelectContent>{allParticipants.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} — {p.ndis_number}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkParticipant(false)}>Cancel</Button>
            <Button onClick={handleLinkParticipant} data-testid="confirm-link-participant">Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link House Dialog */}
      <Dialog open={showLinkHouse} onOpenChange={setShowLinkHouse}>
        <DialogContent>
          <DialogHeader><DialogTitle>Link House</DialogTitle></DialogHeader>
          <div><Label>House *</Label>
            <Select value={linkHouseId} onValueChange={setLinkHouseId}>
              <SelectTrigger data-testid="link-house-select"><SelectValue placeholder="Select house" /></SelectTrigger>
              <SelectContent>{allHouses.map(h => <SelectItem key={h.id} value={h.id}>{h.name || h.house_name || h.id}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkHouse(false)}>Cancel</Button>
            <Button onClick={handleLinkHouse} data-testid="confirm-link-house">Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Message Dialog */}
      <Dialog open={showMessage} onOpenChange={setShowMessage}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send Team Message</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Type</Label>
              <Select value={msgForm.message_type} onValueChange={v => setMsgForm(p => ({ ...p, message_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="GENERAL">General</SelectItem>
                  <SelectItem value="ANNOUNCEMENT">Announcement</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Subject</Label><Input value={msgForm.subject} onChange={e => setMsgForm(p => ({ ...p, subject: e.target.value }))} placeholder="Optional subject" /></div>
            <div><Label>Message *</Label><Textarea value={msgForm.message_body} onChange={e => setMsgForm(p => ({ ...p, message_body: e.target.value }))} rows={4} placeholder="Type your message..." data-testid="message-body-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessage(false)}>Cancel</Button>
            <Button onClick={handleSendMessage} data-testid="confirm-send-message"><Send className="w-4 h-4 mr-2" />Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
